import React, { Component } from 'react';
import { IntlProvider, IntlContext } from 'react-intl';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/de';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/es';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-pluralrules/locale-data/it';
import '@formatjs/intl-pluralrules/locale-data/ja';
import '@formatjs/intl-pluralrules/locale-data/ko';
import '@formatjs/intl-pluralrules/locale-data/pt';
import '@formatjs/intl-pluralrules/locale-data/ru';
import '@formatjs/intl-pluralrules/locale-data/zh';

import '@formatjs/intl-relativetimeformat/polyfill';
import '@formatjs/intl-relativetimeformat/locale-data/de';
import '@formatjs/intl-relativetimeformat/locale-data/en';
import '@formatjs/intl-relativetimeformat/locale-data/es';
import '@formatjs/intl-relativetimeformat/locale-data/fr';
import '@formatjs/intl-relativetimeformat/locale-data/it';
import '@formatjs/intl-relativetimeformat/locale-data/ja';
import '@formatjs/intl-relativetimeformat/locale-data/ko';
import '@formatjs/intl-relativetimeformat/locale-data/pt';
import '@formatjs/intl-relativetimeformat/locale-data/ru';
import '@formatjs/intl-relativetimeformat/locale-data/zh';

import getLocale from 'utils/locale';

import { DEFAULT_INTL_CONFIG } from 'react-intl/src/utils';
import mergeMessages from './mergeMessages';
import { translationFileNames } from './translationFiles';
import styles from './withIntl.module.scss';

export const DEFAULT_LOCALE = 'en';

const importFilesForLocale = (locale = DEFAULT_LOCALE) =>
  translationFileNames.map(name =>
    import(`../../translations/${name}${locale}.json`).catch(() => undefined),
  );

let messagesPromise;
let messagesCache;

function onWarn(message) {
  // suppress annyoing warning message about using the formatjs CLI tool to generate
  // the messages from code. We don't care about that as provide the messages directly from
  // a json file (en.json, fr.json)
  if (
    message.includes(
      '"defaultRichTextElements" was specified but "message" was not pre-compiled.',
    )
  ) {
    return;
  }

  DEFAULT_INTL_CONFIG.onWarn(message);
}

export default ReactComponent =>
  class withIntl extends Component {
    static displayName = `withIntl(${ReactComponent.displayName})`;

    state = {
      locale: getLocale(),
      messages: messagesCache,
    };

    componentDidMount() {
      // Only fetch messages if state is uninitialized or global cache is empty
      if (!this.state.messages) {
        if (!messagesPromise) {
          const languageFiles = [
            // Always load default locale messages
            ...importFilesForLocale(),
            ...(this.state.locale !== DEFAULT_LOCALE
              ? importFilesForLocale(this.state.locale)
              : []),
          ];

          // DO NOT .catch this promise!
          // the setState below will start a render() process
          // if you .catch this, any errors from your state, components, etc.
          // will instead be swallowed in .catch (while promise is active)
          // this generally results in developer confusion as to the origin of the error
          // we let promises warn of any unhandled exceptions inside .then
          // https://github.com/facebook/react/issues/7617#issuecomment-247710003
          messagesPromise = Promise.all(languageFiles).then(
            modules => {
              const filteredModules = modules.filter(module => module);
              // Returned values will be in order of the Promises passed, regardless of completion
              // order. See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/all
              const allTranslations = mergeMessages(filteredModules);
              messagesCache = allTranslations;

              // Add messages for current locale to global scope so they can be accessed by other
              // parts of the web application
              window.XM_currentMessageData = JSON.stringify(allTranslations);

              return allTranslations;
            },
            // we only catch errors from the promise, not the .then handler
            err => {
              /* eslint-disable no-console */
              console.warn('Error processing translation files. Error:', err);
            },
          );
        }

        messagesPromise.then(allTranslations =>
          this.setState({
            messages: allTranslations,
          }),
        );
      }
    }

    render() {
      const { messages, locale } = this.state;

      if (!messages) {
        return null;
      }

      return (
        <IntlContext.Consumer>
          {intl => {
            if (intl) {
              return <ReactComponent {...this.props} />;
            }

            return (
              <IntlProvider
                locale={locale}
                messages={messages}
                key={locale}
                textComponent="span"
                defaultLocale={DEFAULT_LOCALE}
                defaultRichTextElements={{
                  b: chunks => (
                    <strong className={styles.bolded}>{chunks}</strong>
                  ),
                  strong: chunks => (
                    <strong className={styles.bolded}>{chunks}</strong>
                  ),
                  em: chunks => <em>{chunks}</em>,
                  i: chunks => <i>{chunks}</i>,
                  ul: chunks => <ul>{chunks}</ul>,
                  ol: chunks => <ol>{chunks}</ol>,
                  li: chunks => <li>{chunks}</li>,
                  p: chunks => <p>{chunks}</p>,
                }}
                onWarn={onWarn}
              >
                <ReactComponent {...this.props} />
              </IntlProvider>
            );
          }}
        </IntlContext.Consumer>
      );
    }
  };
